import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { featureFlags, notificationPollingIntervalSec } from "../config/featureConfig";
import { useAuth } from "../services/providers/authProvider";
import notificationsService from "../services/notificationsService";

const NotificationUnreadContext = createContext({
  unreadCount: 0,
  refreshUnread: async () => {},
});

export function NotificationUnreadProvider({ children }) {
  const location = useLocation();
  const isImpersonateBootstrap = location.pathname === "/impersonate-login";
  const { token, isImpersonated } = useAuth() || {};
  const [unreadCount, setUnreadCount] = useState(0);
  const pollingTimerRef = useRef(null);
  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!token || !featureFlags.enableNotificationPolling || !featureFlags.enableNotifiche) {
      setUnreadCount(0);
      return;
    }
    try {
      const res = await notificationsService.list(
        {
          page: 0,
          size: 1,
          state: "not-read",
        },
        { silent: true }
      );
      setUnreadCount(Number(res.totalCount) || 0);
    } catch (e) {
      console.warn("notification unread poll failed", e);
      setUnreadCount(0);
    }
  }, [token]);

  useEffect(() => {
    stopPolling();
    if (
      isImpersonateBootstrap
      || !featureFlags.enableNotificationPolling
      || !token
      || !featureFlags.enableNotifiche
    ) {
      setUnreadCount(0);
      return undefined;
    }
    refreshUnread();
    if (isImpersonated) {
      return undefined;
    }
    const ms = Math.max(1, notificationPollingIntervalSec) * 1000;
    pollingTimerRef.current = setInterval(refreshUnread, ms);
    return stopPolling;
  }, [isImpersonateBootstrap, token, isImpersonated, refreshUnread, stopPolling]);

  useEffect(() => {
    const handleLogout = () => {
      stopPolling();
      setUnreadCount(0);
    };
    globalThis.addEventListener("wcp:auth:logout", handleLogout);
    return () => globalThis.removeEventListener("wcp:auth:logout", handleLogout);
  }, [stopPolling]);

  const value = useMemo(
    () => ({ unreadCount, refreshUnread }),
    [unreadCount, refreshUnread]
  );

  return (
    <NotificationUnreadContext.Provider value={value}>
      {children}
    </NotificationUnreadContext.Provider>
  );
}

export function useNotificationUnread() {
  return useContext(NotificationUnreadContext);
}

NotificationUnreadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
